import { StatsigProviderExpo } from '@statsig/expo-bindings';
import * as Updates from 'expo-updates';
import { type ReactNode, useMemo } from 'react';
import { FullScreenSpinner } from 'smoothie';

import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import { STATSIG_TOKEN } from '../../config/config';
import StatsigFacade, {
    StatsigConfig,
} from '../../logger/statsig/StatsigFacade';
import { StatsigStableIdCapture } from '../StatsigStableIdCapture';

interface AnonymousFeatureFlagsProviderProps {
    visitorId: string;
    children: ReactNode;
}

export const AnonymousFeatureFlagsProvider = ({
    visitorId,
    children,
}: AnonymousFeatureFlagsProviderProps) => {
    const statsigUser = {
        custom: {
            isVisitor: true,
            expoUpdateCreatedAt: Updates.createdAt?.toISOString() ?? undefined,
        },
        customIDs: {
            visitorId,
        },
    };

    return (
        <StatsigProviderExpo
            sdkKey={STATSIG_TOKEN}
            user={statsigUser}
            loadingComponent={<FullScreenSpinner />}
            options={StatsigConfig}
        >
            <StatsigStableIdCapture />
            {children}
        </StatsigProviderExpo>
    );
};

interface FeatureFlagsProviderProps {
    children: ReactNode;
}

const FeatureFlagsProvider = ({ children }: FeatureFlagsProviderProps) => {
    const user = useAuthedUser();

    const statsigUser = useMemo(
        () => ({
            userID: user.id,
            email: user.email,
            // A related changed should be made in backend FeatureFlagService.ts
            custom: {
                isVisitor: false,
                category: user.category,
                userType: user.type,
                createdAt: user.createdAt,
                expoUpdateCreatedAt:
                    Updates.createdAt?.toISOString() ?? undefined,
                membershipCreatedAt: user.membership?.createdAt ?? undefined,
                isJointMembership: user.partnerMember != null,
                hasPlusFeatures: user.hasPlusFeatures ?? false,
            },
            customIDs: {
                ...(user.visitorId != null && user.visitorId !== ''
                    ? { visitorId: user.visitorId }
                    : {}),
                ...(user.membership?.id != null
                    ? {
                          membershipId: user.membership.id,
                          guideSignUpId: user.membership.guide?.signUpId ?? '',
                      }
                    : {}),
            },
        }),
        // Intentionally not including everything as we want to prevent unnecessary changes to statsigUser.
        // Changes to statsigUser would likely force the whole app to re-render.
        [user.id, user.email, user.hasPlusFeatures, user.visitorId],
    );

    // A Statsig client for use when hooks are not available.  (Mainly for logging
    // events in the Logger class.)  Always use hooks when appropriate!
    void StatsigFacade.init(statsigUser);

    return (
        <StatsigProviderExpo
            sdkKey={STATSIG_TOKEN}
            user={statsigUser}
            loadingComponent={<FullScreenSpinner />}
            options={StatsigConfig}
        >
            <StatsigStableIdCapture />
            {children}
        </StatsigProviderExpo>
    );
};

export default FeatureFlagsProvider;
